import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import maplibregl from 'maplibre-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CATEGORIES, CATEGORY_META, STYLE_OPTIONS, STYLES } from '../../constants/kart';
import { useKartLocations } from '../../hooks/useKartLocations';
import { buildLabelGeoJSON, createMarkerElement, createPopupContent } from '../../utils/kart';

const LABEL_LAYER_ID = 'kart-labels';

// Delay before a hovered pin opens its bubble — stops bubbles flashing up
// when the cursor merely passes over a pin (e.g. while panning the map).
const HOVER_OPEN_DELAY = 350;

// MapLibre layer filter expression for the active category ('alle' = no filter).
const labelFilterFor = category =>
    category === 'alle' ? null : ['==', ['get', 'category'], category];

export default function Kart() {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const markersRef = useRef({});
    const filterRef = useRef(null);

    // Popup state lives in refs — nothing in the React tree re-renders off it.
    const popupRef = useRef(null);
    const openTimerRef = useRef(null);
    const closeTimerRef = useRef(null);
    const openPopupIdRef = useRef(null);
    const isPinnedRef = useRef(false);

    const { locations, loading } = useKartLocations();

    const [activeCategory, setActiveCategory] = useState('alle');
    const [filterOpen, setFilterOpen] = useState(false);
    const [mapStyle, setMapStyle] = useState('voyager');

    // Mirrors of state, read inside imperative MapLibre callbacks.
    const mapStyleRef = useRef(mapStyle);
    mapStyleRef.current = mapStyle;
    const activeCategoryRef = useRef(activeCategory);
    activeCategoryRef.current = activeCategory;
    const locationsRef = useRef(locations);
    locationsRef.current = locations;

    // ── Close filter dropdown on outside click ───────────────────────
    useEffect(() => {
        if (!filterOpen) return;

        const handleClickOutside = e => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setFilterOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filterOpen]);

    // ── Popup helpers ────────────────────────────────────────────────
    const closePopup = useCallback(() => {
        clearTimeout(openTimerRef.current);
        clearTimeout(closeTimerRef.current);
        popupRef.current?.remove();
    }, []);

    const scheduleClose = useCallback(() => {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = setTimeout(() => closePopup(), 120);
    }, [closePopup]);

    // Hover-bridge: keep an unpinned bubble alive while the cursor is on it.
    const handlePopupPointerEnter = useCallback(() => {
        clearTimeout(closeTimerRef.current);
    }, []);

    const handlePopupPointerLeave = useCallback(() => {
        if (!isPinnedRef.current) scheduleClose();
    }, [scheduleClose]);

    // Lazily create the single shared Popup instance. The 'close' handler
    // is the one place that resets pin state — it fires for every close
    // path (×, map-background click, programmatic remove).
    const getPopup = useCallback(() => {
        if (!popupRef.current) {
            popupRef.current = new maplibregl.Popup({
                closeButton: true,
                closeOnClick: true,
                closeOnMove: false,
                offset: 22,
                maxWidth: '280px',
                className: 'kart-popup',
                focusAfterOpen: false
            });

            popupRef.current.on('close', () => {
                Object.values(markersRef.current).forEach(({ el }) =>
                    el.classList.remove('is-active')
                );
                openPopupIdRef.current = null;
                isPinnedRef.current = false;
            });
        }
        return popupRef.current;
    }, []);

    const openPopup = useCallback(
        (location, markerEl, { pinned }) => {
            if (!map.current) return;

            clearTimeout(closeTimerRef.current);

            Object.values(markersRef.current).forEach(({ el }) => el.classList.remove('is-active'));
            markerEl.classList.add('is-active');

            const popup = getPopup();
            popup.setLngLat(location.coordinates).setDOMContent(createPopupContent(location));

            if (!popup.isOpen()) {
                popup.addTo(map.current);
            }

            openPopupIdRef.current = location.id;
            isPinnedRef.current = pinned;

            const popupEl = popup.getElement();
            if (popupEl) {
                // Stable handlers — re-adding to the same element is a no-op.
                popupEl.addEventListener('pointerenter', handlePopupPointerEnter);
                popupEl.addEventListener('pointerleave', handlePopupPointerLeave);
            }
        },
        [getPopup, handlePopupPointerEnter, handlePopupPointerLeave]
    );

    // ── Add markers ──────────────────────────────────────────────────
    const addMarkers = useCallback(
        locs => {
            if (!map.current) return;

            Object.values(markersRef.current).forEach(({ marker }) => marker.remove());
            markersRef.current = {};

            locs.forEach(location => {
                const el = createMarkerElement(location.category, location.name);

                const marker = new maplibregl.Marker({
                    element: el,
                    anchor: 'center'
                })
                    .setLngLat(location.coordinates)
                    .addTo(map.current);

                // Click / tap pins the bubble open; clicking the same pin
                // again closes it (toggle). Covers keyboard Enter/Space too.
                // stopPropagation keeps the click off the map container —
                // otherwise the popup's closeOnClick would instantly close it.
                el.addEventListener('click', e => {
                    e.stopPropagation();
                    clearTimeout(openTimerRef.current);
                    if (openPopupIdRef.current === location.id && isPinnedRef.current) {
                        closePopup();
                    } else {
                        openPopup(location, el, { pinned: true });
                    }
                });

                // Desktop hover — a fleeting preview after a short delay, so
                // the bubble doesn't flash up when the cursor just passes by.
                el.addEventListener('pointerenter', e => {
                    if (e.pointerType !== 'mouse' || isPinnedRef.current) return;
                    clearTimeout(openTimerRef.current);
                    openTimerRef.current = setTimeout(() => {
                        if (!isPinnedRef.current) openPopup(location, el, { pinned: false });
                    }, HOVER_OPEN_DELAY);
                });

                el.addEventListener('pointerleave', e => {
                    if (e.pointerType !== 'mouse' || isPinnedRef.current) return;
                    clearTimeout(openTimerRef.current);
                    scheduleClose();
                });

                markersRef.current[location.id] = { marker, el, location };
            });
        },
        [openPopup, closePopup, scheduleClose]
    );

    // ── Fit map to markers ───────────────────────────────────────────
    const fitMapToLocations = useCallback(locs => {
        if (!map.current || !locs?.length) return;

        const bounds = new maplibregl.LngLatBounds();

        locs.forEach(location => {
            bounds.extend(location.coordinates);
        });

        const isMobile = window.innerWidth < 768;

        map.current.fitBounds(bounds, {
            padding: isMobile
                ? { top: 80, right: 24, bottom: 80, left: 24 }
                : { top: 100, right: 100, bottom: 100, left: 100 },
            maxZoom: 12,
            duration: 800
        });
    }, []);

    // ── POI name labels (symbol layer — MapLibre handles collision) ──
    // Callers guarantee the style is loaded: the locations effect checks
    // isStyleLoaded(), and the 'style.load' handler fires only when it is.
    const addLabelLayer = useCallback(locs => {
        const m = map.current;
        if (!m || !locs?.length) return;

        const data = buildLabelGeoJSON(locs);

        // Source persists across re-runs but is wiped by setStyle —
        // setData when present, otherwise (re)create source + layer.
        if (m.getSource(LABEL_LAYER_ID)) {
            m.getSource(LABEL_LAYER_ID).setData(data);
            return;
        }

        const dark = mapStyleRef.current === 'dark';
        m.addSource(LABEL_LAYER_ID, { type: 'geojson', data });
        m.addLayer({
            id: LABEL_LAYER_ID,
            type: 'symbol',
            source: LABEL_LAYER_ID,
            layout: {
                'text-field': ['get', 'name'],
                'text-font': ['Open Sans Regular'],
                'text-size': 12,
                'text-anchor': 'top',
                'text-offset': [0, 1.8],
                'text-max-width': 8,
                'text-padding': 4,
                'text-allow-overlap': false
            },
            paint: {
                'text-color': dark ? '#f8f5f3' : '#2d2826',
                'text-halo-color': dark ? '#1a1a1a' : '#ffffff',
                'text-halo-width': 1.6,
                'text-halo-blur': 0.3
            }
        });

        // setFilter accepts null (clears); the addLayer spec does not.
        const filter = labelFilterFor(activeCategoryRef.current);
        if (filter) m.setFilter(LABEL_LAYER_ID, filter);
    }, []);

    // ── Init map ─────────────────────────────────────────────────────
    useEffect(() => {
        if (map.current || !mapContainer.current || loading) return;

        map.current = new maplibregl.Map({
            container: mapContainer.current,
            style: STYLES[mapStyle],
            center: [8.2862, 61.6991],
            zoom: 8,
            minZoom: 5.68,
            maxZoom: 19,
            attributionControl: false
        });

        map.current.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
        map.current.addControl(
            new maplibregl.NavigationControl({ showCompass: false }),
            'bottom-right'
        );

        // (Re)add the label layer whenever the style loads — fires on the
        // first load and again after every style switch (setStyle wipes
        // custom layers). Pairs with the isStyleLoaded() check below.
        map.current.on('style.load', () => addLabelLayer(locationsRef.current));

        return () => {
            clearTimeout(openTimerRef.current);
            clearTimeout(closeTimerRef.current);

            if (popupRef.current) {
                popupRef.current.remove();
                popupRef.current = null;
            }

            Object.values(markersRef.current).forEach(({ marker }) => marker.remove());
            markersRef.current = {};

            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [loading]);

    // ── Update markers when locations change ─────────────────────────
    useEffect(() => {
        if (!map.current || !locations?.length) return;

        addMarkers(locations);
        fitMapToLocations(locations);

        // If the style is already loaded, add labels now; otherwise the
        // 'style.load' handler adds them once the style is ready.
        if (map.current.isStyleLoaded()) addLabelLayer(locations);
    }, [locations, addMarkers, addLabelLayer, fitMapToLocations]);

    // ── Switch map style ─────────────────────────────────────────────
    const handleStyleSwitch = useCallback(
        newStyle => {
            closePopup();
            setMapStyle(newStyle);

            if (!map.current) return;

            // The label layer is re-added by the persistent 'style.load'
            // handler registered in the init effect.
            map.current.once('style.load', () => {
                Object.values(markersRef.current).forEach(({ marker }) =>
                    marker.addTo(map.current)
                );
                fitMapToLocations(locations);
            });

            map.current.setStyle(STYLES[newStyle]);
        },
        [closePopup, fitMapToLocations, locations]
    );

    // ── Filter markers ───────────────────────────────────────────────
    useEffect(() => {
        Object.values(markersRef.current).forEach(({ el, location }) => {
            el.style.display =
                activeCategory === 'alle' || location.category === activeCategory ? '' : 'none';
        });

        if (map.current?.getLayer(LABEL_LAYER_ID)) {
            map.current.setFilter(LABEL_LAYER_ID, labelFilterFor(activeCategory));
        }

        // Close the bubble if its POI was just filtered out.
        const openId = openPopupIdRef.current;
        if (openId != null && activeCategory !== 'alle') {
            const openLoc = markersRef.current[openId]?.location;
            if (openLoc && openLoc.category !== activeCategory) {
                closePopup();
            }
        }
    }, [activeCategory, closePopup]);

    const activeCatLabel = CATEGORIES.find(c => c.id === activeCategory)?.label;

    return (
        <div className="kart">
            <div className="kart__filters" ref={filterRef}>
                <button
                    className={`kart__filter-toggle${filterOpen ? ' is-open' : ''}`}
                    onClick={() => setFilterOpen(prev => !prev)}
                    aria-haspopup="listbox"
                    aria-expanded={filterOpen}
                >
                    {activeCategory !== 'alle' && CATEGORY_META[activeCategory] && (
                        <span
                            className="kart__filter-dot"
                            style={{ backgroundColor: CATEGORY_META[activeCategory].color }}
                        />
                    )}
                    {activeCatLabel}
                    <FontAwesomeIcon icon={faChevronDown} className="kart__filter-chevron" />
                </button>

                {filterOpen && (
                    <ul className="kart__filter-dropdown" role="listbox">
                        {CATEGORIES.map(cat => (
                            <li
                                key={cat.id}
                                role="option"
                                aria-selected={activeCategory === cat.id}
                            >
                                <button
                                    className={`kart__filter-option${activeCategory === cat.id ? ' is-active' : ''}`}
                                    onClick={() => {
                                        setActiveCategory(cat.id);
                                        setFilterOpen(false);
                                    }}
                                >
                                    {cat.id !== 'alle' && CATEGORY_META[cat.id] && (
                                        <span
                                            className="kart__filter-dot"
                                            style={{ backgroundColor: CATEGORY_META[cat.id].color }}
                                        />
                                    )}
                                    {cat.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="kart__style-switcher" role="group" aria-label="Kartlag">
                {STYLE_OPTIONS.map(opt => (
                    <button
                        key={opt.id}
                        className={`kart__style-btn${mapStyle === opt.id ? ' is-active' : ''}`}
                        onClick={() => handleStyleSwitch(opt.id)}
                        title={opt.label}
                    >
                        <FontAwesomeIcon icon={opt.icon} />
                        <span>{opt.label}</span>
                    </button>
                ))}
            </div>

            {loading && (
                <div className="kart__loading" aria-live="polite">
                    <span>Laster kart…</span>
                </div>
            )}

            <div className="kart__map" ref={mapContainer} />
        </div>
    );
}
